import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Assessment,
  TrendingUp,
  People,
  CalendarToday,
  Search
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { obtenerEstadisticasEncuesta } from '../../actions/EncuestaAction';
import { useStateValue } from '../../contexto/store';

// Paleta de colores celestes del tema
const COLORS = [
  '#1976d2', // Primary main
  '#4dabf5', // Primary light
  '#0d47a1', // Primary dark
  '#2196f3', // Info main
  '#64b5f6', // Info light
  '#00bcd4', // Cyan
  '#4fc3f7', // Cyan light
  '#0288d1', // Cyan dark
  '#03a9f4', // Light Blue
  '#29b6f6', // Light Blue light
];

export default function AnaliticasEncuesta(props) {
  // eslint-disable-next-line
  const [{ sesionUsuario }, dispatch] = useStateValue();
  const [estadisticas, setEstadisticas] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [encuestaId] = useState(props.match?.params?.encuestaId || props.encuestaId);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);

  const volverAtras = () => {
    props.history.push(`/respuestas/${encuestaId}`);
  };

  const cargarEstadisticas = async () => {
    setCargando(true);
    setError(null);
    try {
      const fechaInicioStr = fechaInicio ? fechaInicio.toISOString().split('T')[0] : null;
      const fechaFinStr = fechaFin ? fechaFin.toISOString().split('T')[0] : null;
      
      const response = await obtenerEstadisticasEncuesta(encuestaId, fechaInicioStr, fechaFinStr);
      if (response.status === 200) {
        setEstadisticas(response.data);
      } else {
        setError('Error al cargar las estadísticas');
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      setError('No se pudieron cargar las estadísticas. Intente nuevamente.');
    } finally {
      setCargando(false);
    }
  };

  const aplicarFiltros = () => {
    cargarEstadisticas();
  };

  const limpiarFiltros = () => {
    setFechaInicio(null);
    setFechaFin(null);
    setTimeout(() => {
      cargarEstadisticas();
    }, 100);
  };

  useEffect(() => {
    if (encuestaId) {
      cargarEstadisticas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encuestaId]);

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" color="primary">
            Respuestas: {payload[0].value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Porcentaje: {payload[0].payload.porcentaje?.toFixed(2)}%
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (cargando) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Cargando estadísticas...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !estadisticas) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={volverAtras}
          sx={{ mb: 3 }}
        >
          Volver
        </Button>
        <Alert severity="error">{error || 'No se encontraron estadísticas'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={volverAtras}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                borderColor: 'white',
              },
            }}
          >
            Volver
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
              <Assessment sx={{ mr: 1, mb: -0.5 }} />
              Analíticas - {estadisticas.tituloEncuesta}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
              {estadisticas.descripcionEncuesta}
            </Typography>
            <Chip
              label={`Encuesta ${estadisticas.numeroEncuesta}`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 500,
                backdropFilter: 'blur(10px)',
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Filtros de fecha */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Filtrar por Rango de Fechas
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Fecha Inicio"
              value={fechaInicio}
              onChange={(newValue) => setFechaInicio(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Fecha Fin"
              value={fechaFin}
              onChange={(newValue) => setFechaFin(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={aplicarFiltros}
                disabled={cargando}
              >
                Aplicar Filtros
              </Button>
              <Button
                variant="outlined"
                onClick={limpiarFiltros}
                disabled={cargando}
              >
                Limpiar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Respuestas
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {estadisticas.totalRespuestas}
                  </Typography>
                </Box>
                <People sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4dabf5 0%, #2196f3 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Preguntas
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {estadisticas.estadisticasPreguntas?.length || 0}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #64b5f6 0%, #1976d2 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Primera Respuesta
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {estadisticas.primeraRespuesta
                      ? new Date(estadisticas.primeraRespuesta).toLocaleDateString('es-ES')
                      : 'N/A'}
                  </Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4fc3f7 0%, #0288d1 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Última Respuesta
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {estadisticas.ultimaRespuesta
                      ? new Date(estadisticas.ultimaRespuesta).toLocaleDateString('es-ES')
                      : 'N/A'}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Período de análisis */}
      {(estadisticas.fechaInicioFiltro || estadisticas.fechaFinFiltro) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Período de análisis:</strong>{' '}
            {estadisticas.fechaInicioFiltro
              ? new Date(estadisticas.fechaInicioFiltro).toLocaleDateString('es-ES')
              : 'Inicio'}{' '}
            -{' '}
            {estadisticas.fechaFinFiltro
              ? new Date(estadisticas.fechaFinFiltro).toLocaleDateString('es-ES')
              : 'Fin'}
          </Typography>
        </Alert>
      )}

      {/* Estadísticas por pregunta */}
      {estadisticas.estadisticasPreguntas && estadisticas.estadisticasPreguntas.length > 0 ? (
        estadisticas.estadisticasPreguntas.map((pregunta, index) => (
          <Paper
            key={pregunta.preguntaId}
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Chip
                label={`Pregunta ${pregunta.orden}`}
                size="small"
                color="primary"
                sx={{ mb: 1 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {pregunta.textoPregunta}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de respuestas: {pregunta.totalRespuestas}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Gráfico de pastel */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
                Distribución de Respuestas
              </Typography>
              <Box sx={{ width: '100%', height: { xs: 280, sm: 300, md: 320 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pregunta.opciones}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ porcentaje }) => `${porcentaje.toFixed(1)}%`}
                      outerRadius="75%"
                      fill="#8884d8"
                      dataKey="cantidad"
                      nameKey="texto"
                    >
                      {pregunta.opciones.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                      iconSize={10}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Gráfico de barras */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
                Cantidad de Respuestas
              </Typography>
              <Box sx={{ width: '100%', height: { xs: 280, sm: 300, md: 320 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pregunta.opciones}
                    margin={{ top: 15, right: 30, left: 10, bottom: 65 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="texto"
                      angle={-35}
                      textAnchor="end"
                      height={65}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tick={{ fontSize: 11 }} width={45} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cantidad" name="Respuestas" radius={[6, 6, 0, 0]} barSize={55}>
                      {pregunta.opciones.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Tabla de detalle */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Detalle de Respuestas
              </Typography>
              <Grid container spacing={2}>
                {[...pregunta.opciones]
                  .sort((a, b) => b.cantidad - a.cantidad)
                  .map((opcion, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={opcion.opcionRespuestaId}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: `4px solid ${COLORS[idx % COLORS.length]}`,
                        transition: 'all 0.2s',
                        height: '100%',
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          gutterBottom
                          sx={{ 
                            minHeight: 36,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '0.875rem',
                          }}
                        >
                          {opcion.texto}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, my: 1 }}>
                          {opcion.cantidad}
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                          {opcion.porcentaje.toFixed(2)}%
                        </Typography>
                        <Box
                          sx={{
                            height: 6,
                            backgroundColor: 'action.hover',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${opcion.porcentaje}%`,
                              backgroundColor: COLORS[idx % COLORS.length],
                              transition: 'width 0.5s ease-in-out',
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        ))
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No hay estadísticas disponibles para esta encuesta.
          </Typography>
        </Paper>
      )}
    </Container>
  );
}
